<template>
  <div class="connection-status">
    <h3>ServiceM8 Connection</h3>
    <div v-if="loading" class="status-loading">Checking connection...</div>
    <div v-else-if="connected" class="status-card" :class="{ expanded: isExpanded, 'broken-connection': !isValid }">
      <div class="status-header" @click="toggleExpand">
        <span class="status-indicator" :class="isValid ? 'connected' : 'broken'"></span>
        <div class="status-main">
          <span class="status-label">{{ isValid ? 'Connected' : 'Connection Broken' }}</span>
          <span v-if="accountInfo && accountInfo.name" class="account-name">{{ accountInfo.name }}</span>
          <span v-else class="account-name-placeholder">{{ isValid ? 'Account connected' : 'Reconnection required' }}</span>
        </div>
        <div class="status-actions">
          <span class="expand-icon">{{ isExpanded ? '▼' : '▶' }}</span>
          <button 
            v-if="!isValid" 
            @click.stop="reconnect" 
            class="btn-reconnect"
          >
            Reconnect
          </button>
          <button 
            v-else 
            @click.stop="disconnect" 
            class="btn-disconnect"
          >
            Disconnect
          </button>
        </div>
      </div>
      <div v-if="isExpanded && accountInfo" class="status-details">
        <div class="detail-row">
          <strong>Account Name:</strong>
          <span>{{ accountInfo.name || 'N/A' }}</span>
        </div>
        <div class="detail-row">
          <strong>Email:</strong>
          <span>{{ accountInfo.email || 'N/A' }}</span>
        </div>
        <div class="detail-row">
          <strong>Account UUID:</strong>
          <span class="uuid-text">{{ accountInfo.uuid || 'N/A' }}</span>
        </div>
      </div>
    </div>
    <div v-else class="status-disconnected">
      <span class="status-indicator disconnected"></span>
      <span>Not Connected</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConnectionStatus',
  data() {
    return {
      connected: false,
      isValid: true,
      loading: true,
      accountInfo: null,
      isExpanded: false,
    }
  },
  async mounted() {
    await this.checkStatus();
  },
  methods: {
    async checkStatus() {
      this.loading = true;
      try {
        const response = await fetch('/servicem8/status', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-sso-token': await this.getSSOToken(),
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          this.connected = data.connected;
          this.isValid = data.accountValid !== false;
          // Store account info if available (prefer currentAccountInfo from API, fallback to stored accountInfo)
          // If both are null but connected, try to use accountInfo from connection
          if (data.connected) {
            this.accountInfo = data.currentAccountInfo || data.accountInfo || null;
            // If still no account info but connected, show a placeholder
            if (!this.accountInfo) {
              console.warn('ServiceM8 connected but no account info available');
            }
          } else {
            this.accountInfo = null;
          }
          // Emit status change event with both connected and valid status
          this.$emit('status-changed', { connected: data.connected, isValid: this.isValid });
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
        this.connected = false;
        this.accountInfo = null;
      } finally {
        this.loading = false;
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    async reconnect() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/auth', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.authUrl) {
          // Open OAuth in a popup window
          console.log('Opening ServiceM8 OAuth in popup:', data.authUrl);
          this.openOAuthPopup(data.authUrl);
        } else {
          throw new Error('No authorization URL received from server');
        }
      } catch (error) {
        console.error('Error reconnecting ServiceM8:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to initiate ServiceM8 reconnection';
        alert(`Failed to reconnect ServiceM8: ${errorMessage}`);
      }
    },
    openOAuthPopup(authUrl) {
      // Calculate popup dimensions (centered on screen)
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      // Open popup window
      const popup = window.open(
        authUrl,
        'ServiceM8 OAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      
      if (!popup) {
        alert('Popup blocked. Please allow popups for this site and try again.');
        return;
      }
      
      // Listen for messages from the popup
      const messageListener = (event) => {
        // Verify origin for security (adjust to match your domain)
        if (event.origin !== window.location.origin) {
          return;
        }
        
        if (event.data.type === 'SERVICEM8_OAUTH_SUCCESS') {
          console.log('ServiceM8 OAuth successful');
          window.removeEventListener('message', messageListener);
          popup.close();
          
          // Refresh connection status
          this.checkStatus();
          
          // Emit event to parent component to refresh connection status
          // checkStatus will emit the proper status
        } else if (event.data.type === 'SERVICEM8_OAUTH_ERROR') {
          console.error('ServiceM8 OAuth error:', event.data.error);
          window.removeEventListener('message', messageListener);
          popup.close();
          alert(`ServiceM8 reconnection failed: ${event.data.error}`);
        }
      };
      
      window.addEventListener('message', messageListener);
      
      // Check if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
        }
      }, 500);
    },
    async disconnect() {
      if (!confirm('Are you sure you want to disconnect ServiceM8? This will stop all syncing.')) {
        return;
      }
      try {
        const response = await fetch('/servicem8/disconnect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-sso-token': await this.getSSOToken(),
          },
        });
        
        if (response.ok) {
          this.connected = false;
          this.isValid = true;
          this.accountInfo = null;
          this.isExpanded = false;
          // Emit status change event
          this.$emit('status-changed', { connected: false, isValid: true });
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || errorData.message || 'Failed to disconnect');
        }
      } catch (error) {
        console.error('Error disconnecting:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to disconnect ServiceM8. Please try again.';
        alert(errorMessage);
      }
    },
    async getSSOToken() {
      // Get SSO token from parent window
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    }
  }
}
</script>

<style scoped>
.connection-status {
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.status-loading {
  color: #666;
}

.status-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.status-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #24acfe;
}

.status-card.expanded {
  border-color: #24acfe;
  box-shadow: 0 4px 12px rgba(36, 172, 254, 0.15);
}

.status-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-label {
  font-weight: 500;
  color: #2c3e50;
}

.account-name {
  font-size: 14px;
  color: #24acfe;
  font-weight: 600;
}

.account-name-placeholder {
  font-size: 13px;
  color: #666;
  font-style: italic;
}

.status-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.expand-icon {
  color: #666;
  font-size: 12px;
  user-select: none;
}

.status-details {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  gap: 10px;
  font-size: 13px;
}

.detail-row strong {
  min-width: 120px;
  color: #2c3e50;
  font-weight: 600;
}

.detail-row span {
  color: #666;
  word-break: break-word;
}

.uuid-text {
  font-family: monospace;
  font-size: 12px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
}

.status-disconnected {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.connected {
  background-color: #4caf50;
}

.status-indicator.broken {
  background-color: #f44336;
}

.status-indicator.disconnected {
  background-color: #9e9e9e;
}

.status-card.broken-connection {
  border-color: #ef5350;
  background-color: #fff9f9;
}

.status-card.broken-connection .status-label {
  color: #c62828;
  font-weight: bold;
}

.btn-disconnect {
  margin-left: auto;
  padding: 6px 12px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-disconnect:hover {
  background: #d32f2f;
}

.btn-reconnect {
  margin-left: auto;
  padding: 6px 12px;
  background: linear-gradient(135deg, #009dff 0%, #00b8ff 50%, #00e3ff 100%);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.2s;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.3);
}

.btn-reconnect:hover {
  background: linear-gradient(315deg, #00e3ff 0%, #00b8ff 50%, #009dff 100%);
}
</style>

